import Phaser from "phaser";
import type { CharacterGridSpec } from "@/character/grid";
import type { CharacterProgression } from "@/game/model/character-progression";
import { attackGapMs } from "@/game/model/character-stats";
import { registerCharacterSceneAnims } from "@/game/register-character-scene-anims";
import { SceneSkillFx } from "@/game/skills/scene-skill-fx";
import type { HomeSkillRuntimePayload } from "@/home/home-events";
import {
  PET_ANIM_PREFIX,
  PET_ATTACK_RANGE,
  PET_DEPTH_BEHIND,
  PET_FOLLOW_LERP,
  PET_FOLLOW_OFFSET_X,
  PET_FOLLOW_OFFSET_Y,
  PET_SKILL_RANGE,
  PET_SHEET_TEXTURE,
} from "@/game/pet/pet-constants";
import { petCombatRatings } from "@/pet/pet-combat-ratings";
import { petDisplayScaleMultiplier } from "@/pet/pet-size-scale";
import type { PetDefinition } from "@/pet/types";

export type PetStrikeTarget = {
  gfx: Phaser.GameObjects.GameObject & { x: number; y: number; setDepth: (d: number) => void };
  ratings: import("@/game/model/character-stats").CombatRatings;
  hp: number;
};

export type PlayerPetCompanionOptions = {
  progression: CharacterProgression;
  pet: PetDefinition;
  skillRuntime?: HomeSkillRuntimePayload;
  onStrike?: (target: PetStrikeTarget, damageMultiplier?: number) => void;
  battleActive?: () => boolean;
  /** Scene-specific sort (grid row or world Y). Default: player.depth − PET_DEPTH_BEHIND. */
  resolveDisplayDepth?: (
    pet: Phaser.GameObjects.Sprite,
    player: Phaser.GameObjects.Sprite,
  ) => number;
};

export type PlayerPetCompanion = {
  sprite: Phaser.GameObjects.Sprite;
  skillFx: SceneSkillFx;
  tick: (delta: number, player: Phaser.GameObjects.Sprite) => void;
  setBuffBonus: (bonus: Partial<import("@/game/model/character-stats").StatAllocation> | undefined) => void;
  setTargetFinder: (fn: (range: number) => PetStrikeTarget | null) => void;
  setStrikeResolver: (fn: (target: PetStrikeTarget, mult?: number) => void) => void;
  destroy: () => void;
};

function ensurePetSceneAnims(scene: Phaser.Scene): void {
  const grid = scene.registry.get("petSpritesheetGrid") as CharacterGridSpec | undefined;
  if (!grid) return;
  registerCharacterSceneAnims(scene, PET_SHEET_TEXTURE, PET_ANIM_PREFIX, grid);
}

function applyPetScale(sprite: Phaser.GameObjects.Sprite, scene: Phaser.Scene, pet: PetDefinition): void {
  const grid = scene.registry.get("petSpritesheetGrid") as { frameHeight: number } | undefined;
  const targetHeight =
    (scene.registry.get("playerDisplayHeightPx") as number | undefined) ?? 96;
  const baseScale = grid ? targetHeight / grid.frameHeight : 2;
  sprite.setScale(baseScale * petDisplayScaleMultiplier(pet));
}

export function petTexturesEnabled(scene: Phaser.Scene): boolean {
  return scene.textures.exists(PET_SHEET_TEXTURE) || scene.textures.exists("pet-idle");
}

export function attachPlayerPetCompanion(
  scene: Phaser.Scene,
  player: Phaser.GameObjects.Sprite,
  options: PlayerPetCompanionOptions,
): PlayerPetCompanion | null {
  if (!petTexturesEnabled(scene)) return null;
  ensurePetSceneAnims(scene);
  const textureKey = scene.textures.exists(PET_SHEET_TEXTURE) ? PET_SHEET_TEXTURE : "pet-idle";
  const petSprite = scene.add.sprite(player.x, player.y, textureKey, 0);
  petSprite.setOrigin(0.5, 1);
  applyPetScale(petSprite, scene, options.pet);
  const playerIndex = scene.children.getIndex(player);
  if (playerIndex >= 0) {
    scene.children.moveTo(petSprite, playerIndex);
  }
  petSprite.setDepth(
    options.resolveDisplayDepth?.(petSprite, player) ?? player.depth - PET_DEPTH_BEHIND,
  );
  const idleKey = `${PET_ANIM_PREFIX}idle`;
  if (scene.anims.exists(idleKey)) {
    petSprite.play(idleKey);
  }

  let buffBonus: Partial<import("@/game/model/character-stats").StatAllocation> | undefined;
  let attackCooldownMs = 0;
  let attackingAnim = false;
  let strikeResolver = options.onStrike;
  let nearestResolver: ((range: number) => PetStrikeTarget | null) | null = null;

  const skillFx = new SceneSkillFx(scene, () => petSprite);
  if (options.skillRuntime) {
    skillFx.setRuntime(options.skillRuntime);
  }

  const slotCooldownUntil: number[] = [];
  const slotPending: boolean[] = [];

  const ratings = () => petCombatRatings(options.progression, options.pet.statAllocation, buffBonus);

  const followPlayer = (p: Phaser.GameObjects.Sprite) => {
    const offsetX = p.flipX ? -PET_FOLLOW_OFFSET_X : PET_FOLLOW_OFFSET_X;
    const targetX = p.x + offsetX;
    const targetY = p.y - PET_FOLLOW_OFFSET_Y;
    petSprite.x += (targetX - petSprite.x) * PET_FOLLOW_LERP;
    petSprite.y += (targetY - petSprite.y) * PET_FOLLOW_LERP;
    petSprite.setFlipX(p.flipX);
    petSprite.setVisible(p.visible);
    petSprite.setDepth(
      options.resolveDisplayDepth?.(petSprite, p) ?? p.depth - PET_DEPTH_BEHIND,
    );
    const moved = Math.hypot(targetX - petSprite.x, targetY - petSprite.y) > 2;
    const runKey = `${PET_ANIM_PREFIX}run`;
    const idle = `${PET_ANIM_PREFIX}idle`;
    if (!attackingAnim) {
      if (moved && scene.anims.exists(runKey)) {
        if (petSprite.anims.currentAnim?.key !== runKey) petSprite.play(runKey, true);
      } else if (scene.anims.exists(idle)) {
        if (petSprite.anims.currentAnim?.key !== idle) petSprite.play(idle, true);
      }
    }
  };

  const playPetAttack = () => {
    const atk = `${PET_ANIM_PREFIX}attack`;
    if (!scene.anims.exists(atk)) return;
    attackingAnim = true;
    petSprite.play(atk);
    petSprite.once("animationcomplete", (anim: Phaser.Animations.Animation) => {
      if (anim.key !== atk) return;
      attackingAnim = false;
      if (scene.anims.exists(idleKey)) petSprite.play(idleKey, true);
    });
  };

  const tryAutoAttack = (delta: number) => {
    if (!options.battleActive?.()) return;
    attackCooldownMs = Math.max(0, attackCooldownMs - delta);
    if (attackCooldownMs > 0 || attackingAnim || !strikeResolver) return;
    const target = nearestResolver?.(PET_ATTACK_RANGE) ?? null;
    if (!target || target.hp <= 0) return;
    const gap = attackGapMs(ratings().level, ratings().attackSpeed);
    attackCooldownMs = gap;
    playPetAttack();
    strikeResolver(target, 1);
  };

  const tryAutoSkills = () => {
    if (!options.battleActive?.() || !options.skillRuntime) return;
    const bar = options.pet.skillBar;
    if (!bar.masterAuto) return;
    const now = Date.now();
    for (let i = 0; i < bar.slots.length; i += 1) {
      if (!bar.slotAuto[i]) continue;
      const skillId = bar.slots[i];
      if (!skillId) continue;
      if (slotPending[i]) continue;
      if ((slotCooldownUntil[i] ?? 0) > now) continue;
      const skill = options.skillRuntime.skills.find((s) => s.id === skillId);
      if (!skill) continue;
      if (skill.kind === "buff") {
        if (skillFx.hasActiveBuff()) continue;
        slotPending[i] = true;
        skillFx.playSkill(skillId, () => {
          slotPending[i] = false;
          slotCooldownUntil[i] = now + skill.cooldownMs;
          buffBonus = skill.buffStatBonus ?? undefined;
        });
        return;
      }
      const target = nearestResolver?.(PET_SKILL_RANGE);
      if (!target) continue;
      slotPending[i] = true;
      skillFx.playSkill(skillId, (hitSkill, mult) => {
        slotPending[i] = false;
        slotCooldownUntil[i] = now + hitSkill.cooldownMs;
        strikeResolver?.(target, mult);
      });
      return;
    }
  };

  const tick = (delta: number, p: Phaser.GameObjects.Sprite) => {
    if (!petSprite.active) return;
    followPlayer(p);
    skillFx.tickFollowPlayer();
    tryAutoAttack(delta);
    tryAutoSkills();
  };

  return {
    sprite: petSprite,
    skillFx,
    tick,
    setBuffBonus: (b) => {
      buffBonus = b;
    },
    setTargetFinder: (fn) => {
      nearestResolver = fn;
    },
    setStrikeResolver: (fn) => {
      strikeResolver = fn;
    },
    destroy: () => {
      skillFx.destroy();
      petSprite.destroy();
    },
  };
}
